import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

interface FieldProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  // The render callback receives the input id and an `aria-describedby` value
  // that points at the visible error or hint paragraph (or undefined if there
  // isn't one). Spread it onto the rendered input/select so screen readers
  // announce error and hint text alongside the label (WCAG 2.2 AA).
  children: (id: string, descProps: { 'aria-describedby'?: string }) => ReactNode;
}

export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  const descId = useId();
  const showDesc = error != null || hint != null;
  const descProps = showDesc ? { 'aria-describedby': descId } : {};
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
      </label>
      {children(id, descProps)}
      {error ? (
        <p id={descId} className="text-sm text-traffic-red" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

// 16px (text-base) on inputs is intentional — anything smaller triggers
// iOS Safari's zoom-on-focus, which is exactly the friction DESIGN §1.1
// is trying to remove for managers logging in under stress.
const inputClasses = [
  'rounded-md border border-border bg-background px-3 py-2.5 text-base text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
  'disabled:cursor-not-allowed disabled:opacity-60',
].join(' ');

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={[inputClasses, props.className ?? ''].join(' ')} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={[inputClasses, props.className ?? ''].join(' ')} />;
}
